import React, { useMemo, useEffect, useState } from 'react';
import { Button, CustomCheckbox, CustomSelect, CustomSelectItem, FeatureFlagInfoWrapper } from '@hwm/ui-lib';
import { endOfDay } from 'date-fns';
import { CustomTooltip, DatePicker } from '@shared/components';
import { usePeriodFilterState, useSharedFilterState } from '@shared/hooks';
import { Menu, Theme, useMediaQuery, useTheme, Typography, Collapse } from '@material-ui/core';
import { FilterList, Refresh } from '@material-ui/icons';
import { useLocation } from 'react-router-dom';
import { AppPaths } from '@core/routes/routesPaths';
import {
  ALARM_FILTERS_MENU_WIDTH,
  AlarmsAcknowledgeOptionsFn,
  DataOptionsFn,
  GRAPH_FILTERS_MENU_WIDTH,
  IntervalOptionsFn,
} from './Filters.utils';
import { t, Trans } from '@lingui/macro';
import { GraphUnits } from './GraphUnits';
import { useGraphFilterState } from '@features/DataView/useGraphFilterState';
import { useHistoryFilterState } from '@features/History/useHistoryFilterState';
import { useGetByIds } from '@core/store/hooks/entityHooks';
import { siteReducer } from '@core/store/entity/sites/SitesReducer';
import { EntityReducers } from '@core/store/EntityReducer';
import { useAlarmsFilterState } from '@features/Alarms/useAlarmsFilterState';
import { isEmpty } from 'lodash';
import { useAppSelector } from '@core/store/store';
import { FeatureFlagsEnum, TimeRangeOptionsFn, TimeRangeType } from '@shared/models';
import { SitesSelector } from './SitesSelecotr';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelector';
import { useFilterStyles } from './Filters.styles';

export const Filters: React.FC = () => {
  const theme = useTheme<Theme>();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const graphFiltersScreenSize = useMediaQuery(theme.breakpoints.down(GRAPH_FILTERS_MENU_WIDTH));
  const alarmFiltersScreenSize = useMediaQuery(theme.breakpoints.down(ALARM_FILTERS_MENU_WIDTH));

  const location = useLocation();

  const classes = useFilterStyles();

  const { sharedState, setSitesFilter, setRefreshButton } = useSharedFilterState();
  const { periodState, setPeriodFilter } = usePeriodFilterState();

  const { graphState, setIntervalFilter, setDataTypeFilter, setShowAsPercentageFilter, setIncludeCalibrationHistory } =
    useGraphFilterState();
  const { historyState, setUserFilter } = useHistoryFilterState();
  const { alarmsState, setConditionFilter, setAcknowledgeFilter, setShowSystemAlarmsFilter } = useAlarmsFilterState();

  const { preferences: preferencesValues } = useAppSelector(preferencesSelector);

  const userSites = useGetByIds(
    siteReducer.entityAdapter,
    EntityReducers.sites,
    (state) => state['user-sites-ui'].loadedIds
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const showPeriodFilter = useMemo(
    () =>
      location.pathname !== AppPaths.siteInformation.path.base && location.pathname !== AppPaths.reporting.path.base,
    [location.pathname]
  );

  const filterStateDates: [Date, Date] = useMemo(
    () => (periodState.from && periodState.to ? [new Date(periodState.from), new Date(periodState.to)] : null),
    [periodState.from, periodState.to]
  );

  const showGraphFilters = useMemo(
    () => location.pathname === AppPaths.dataView.path.base && graphState.interval && graphState.dataType,
    [location.pathname, graphState.interval, graphState.dataType]
  );

  const showGraphFiltersInMenu = useMemo(
    () => graphFiltersScreenSize && showGraphFilters,
    [graphFiltersScreenSize, showGraphFilters]
  );

  const showSiteInformationFilters = useMemo(
    () => location.pathname === AppPaths.siteInformation.path.base,
    [location.pathname]
  );

  const showAlarmsFilters = useMemo(() => location.pathname === AppPaths.alarms.path.base, [location.pathname]);

  const showAlarmsFiltersInMenu = useMemo(
    () => alarmFiltersScreenSize && showAlarmsFilters,
    [alarmFiltersScreenSize, showAlarmsFilters]
  );

  const showHistoryFilters = useMemo(() => location.pathname === AppPaths.history.path.base, [location.pathname]);

  const showReportingFilters = useMemo(() => location.pathname === AppPaths.reporting.path.base, [location.pathname]);

  useEffect(() => {
    if (!showGraphFilters && !showReportingFilters) {
      setRefreshButton({
        show: false,
        disabled: true,
        onClick: () => {},
      });
    }
  }, [showGraphFilters, showReportingFilters, location.pathname, setRefreshButton]);

  // preferences
  useEffect(() => {
    setPeriodFilter(TimeRangeOptionsFn()[preferencesValues?.timeRange ?? TimeRangeType.Last7days].range);
  }, [setPeriodFilter, preferencesValues?.timeRange]);

  useEffect(() => {
    if (!preferencesValues || isEmpty(preferencesValues)) return;

    if (preferencesValues.graphInterval) {
      setIntervalFilter(preferencesValues.graphInterval);
    }
    if (preferencesValues.graphDataType) {
      setDataTypeFilter(preferencesValues.graphDataType);
    }
    if (
      preferencesValues.graphShowBoundariesAsPercentage &&
      graphState.channelTypeUnitsAndPercentage?.canShowPercentage
    ) {
      setShowAsPercentageFilter(preferencesValues.graphShowBoundariesAsPercentage);
    }
    if (preferencesValues.alarmCondition) {
      setConditionFilter(preferencesValues.alarmCondition);
    }
    if (preferencesValues.alarmAcknowledge) {
      setAcknowledgeFilter(preferencesValues.alarmAcknowledge);
    }
    if (preferencesValues.alarmShowSystem) {
      setShowSystemAlarmsFilter(preferencesValues.alarmShowSystem);
    }
  }, [
    preferencesValues,
    graphState.channelTypeUnitsAndPercentage?.canShowPercentage,
    setIntervalFilter,
    setDataTypeFilter,
    setConditionFilter,
    setAcknowledgeFilter,
    setShowSystemAlarmsFilter,
    setShowAsPercentageFilter,
  ]);

  // selected sites
  useEffect(() => {
    setSitesFilter(null);
  }, [userSites.length, setSitesFilter]);

  // if number of sites didn't change ->
  // check if ids are the same ->
  // if not there is a need for update (set to null)
  useEffect(() => {
    if (
      sharedState?.sites !== null &&
      sharedState?.sites?.length === userSites?.length &&
      !sharedState?.sites?.every(({ id }) => userSites.some(({ id: siteId }) => siteId === id))
    ) {
      setSitesFilter(null);
    }
  }, [userSites, sharedState?.sites, setSitesFilter]);

  useEffect(() => {
    if (sharedState.sites === null) {
      setSitesFilter(userSites?.map(({ id, siteID }) => ({ id, name: siteID })) ?? []);
    }
  }, [sharedState.sites, userSites, setSitesFilter]);

  useEffect(() => {
    if (showSiteInformationFilters && sharedState.sites?.length !== 1 && userSites?.length > 0) {
      setSitesFilter([{ id: userSites[0].id, name: userSites[0].siteID }]);
    }
  }, [setSitesFilter, showSiteInformationFilters, userSites, sharedState.sites?.length]);

  useEffect(
    () => setAnchorEl(null),
    [smallScreen, alarmFiltersScreenSize, graphFiltersScreenSize, showGraphFiltersInMenu, showAlarmsFiltersInMenu]
  );

  const periodFilterComponent = useMemo(
    () => [
      <DatePicker
        dataTestId="main-filters-period-date-picker"
        values={filterStateDates}
        onChange={setPeriodFilter}
        newEditableLook
        clearable={false}
        maxDate={endOfDay(new Date())}
        openToDate={filterStateDates?.[1] ?? undefined}
        monthAndYearSelect
      />,
    ],
    [filterStateDates, setPeriodFilter]
  );

  const graphFilterComponents = useMemo(
    () => [
      <CustomSelect
        dataTestId="main-filters-interval-select"
        label={t`Interval`}
        placeholder="Please Select"
        onChange={(selectedValue) => setIntervalFilter((selectedValue as CustomSelectItem).value)}
        options={Object.entries(IntervalOptionsFn()).map(([value, label]) => ({
          label,
          value,
        }))}
        minWidth={188}
        className={classes.select}
        value={{
          label: IntervalOptionsFn()[graphState.interval],
          value: graphState.interval,
        }}
        isDisabled={false}
        isError={false}
        newEditableLook
      />,
      <CustomSelect
        dataTestId="main-filters-datatype-select"
        label={t`Data`}
        placeholder="Please Select"
        onChange={(selectedValue) => setDataTypeFilter((selectedValue as CustomSelectItem).value)}
        options={Object.entries(DataOptionsFn()).map(([value, label]) => ({
          label,
          value,
        }))}
        minWidth={172}
        className={classes.select}
        value={{
          label: DataOptionsFn()[graphState.dataType],
          value: graphState.dataType,
        }}
        isDisabled={false}
        isError={false}
        newEditableLook
      />,
      <GraphUnits />,
      <FeatureFlagInfoWrapper
        flag={FeatureFlagsEnum.CalibrationHistory}
        modalTitle={undefined}
        info={{
          title: undefined,
          description: undefined,
          readMoreAtLink: '',
          footer: undefined,
        }}
      >
        {({ enabled }) => (
          <CustomCheckbox
            dataTestId="main-filters-includeCalibrationHistory-checkbox"
            label={t`Calibration History`}
            checked={Boolean(graphState.includeCalibrationHistory)}
            onChange={(_, check) => {
              setIncludeCalibrationHistory(check);
            }}
            disabled={!enabled}
            newEditableLook
          />
        )}
      </FeatureFlagInfoWrapper>,
      // TODO: this is hidden until we have correctly implemented percentage values for all data
      // and scales on graph - http://hwm-svn.palmer.lan:8484/browse/DG3DV-133
      // ...(graphState.channelTypeUnitsAndPercentage?.canShowPercentage
      //   ? [
      //       <CustomCheckbox
      //         label={t`Alarm Boundaries As Percentage`}
      //         checked={Boolean(graphState.showAlarmBoundariesAsPercentage)}
      //         onChange={(_, check) => setShowAsPercentageFilter(check)}
      //         containerClasses={classes.checkboxGraph}
      //         newEditableLook
      //       />,
      //     ]
      //   : []),
    ],
    [
      classes.select,
      // classes.checkboxGraph,
      graphState.dataType,
      graphState.interval,
      graphState.includeCalibrationHistory,
      // graphState.showAlarmBoundariesAsPercentage,
      // graphState.channelTypeUnitsAndPercentage?.canShowPercentage,
      setDataTypeFilter,
      setIntervalFilter,
      setIncludeCalibrationHistory,
      // setShowAsPercentageFilter,
    ]
  );

  const historyFilterComponents = useMemo(
    () => [
      <CustomSelect
        dataTestId="main-filters-user-select"
        label={t`User`}
        placeholder={t`Select user`}
        onChange={(selectedValue) => setUserFilter((selectedValue as CustomSelectItem).value)}
        options={[
          {
            label: t`All`,
            value: 'All',
          },
          ...(historyState.usersList.length > 0
            ? historyState.usersList.map((el) => ({
                label: el,
                value: el,
              }))
            : []),
        ]}
        minWidth={160}
        className={classes.select}
        value={
          historyState.user
            ? {
                label: historyState.user,
                value: historyState.user,
              }
            : null
        }
        isDisabled={false}
        isError={false}
        newEditableLook
      />,
    ],
    [classes.select, historyState.user, historyState.usersList, setUserFilter]
  );

  const alarmsFilterComponents = useMemo(
    () => [
      <CustomSelect
        dataTestId="main-filters-condition-select"
        label={t`Condition`}
        placeholder={t`Select`}
        onChange={(selectedValue) => setConditionFilter((selectedValue as CustomSelectItem)?.value ?? null)}
        clearable
        options={
          alarmsState.filterOptions?.conditions?.length > 0
            ? alarmsState.filterOptions.conditions.map((el) => ({
                label: el,
                value: el,
              }))
            : []
        }
        minWidth={228}
        className={`${classes.select} ${classes.alarmsSelect}`}
        value={
          alarmsState.conditionFilter
            ? {
                label: alarmsState.conditionFilter,
                value: alarmsState.conditionFilter,
              }
            : null
        }
        isDisabled={false}
        isError={false}
        newEditableLook
      />,
      <CustomSelect
        dataTestId="main-filters-acknowledge-select"
        label={t`Acknowledge`}
        placeholder={t`Select acknowledge`}
        onChange={(selectedValue) => setAcknowledgeFilter((selectedValue as CustomSelectItem).value)}
        options={Object.entries(AlarmsAcknowledgeOptionsFn()).map(([value, label]) => ({
          label,
          value,
        }))}
        minWidth={252}
        className={`${classes.select} ${classes.alarmsSelect}`}
        value={
          alarmsState.filter
            ? {
                label: alarmsState.filter,
                value: alarmsState.filter,
              }
            : null
        }
        isDisabled={false}
        isError={false}
        newEditableLook
      />,
      <CustomCheckbox
        dataTestId="main-filters-sysalarms-checkbox"
        label={t`System Alarms`}
        checked={Boolean(alarmsState.showSystemAlarms)}
        onChange={(_, check) => setShowSystemAlarmsFilter(check)}
        wrapperClasses={classes.checkbox}
        newEditableLook
      />,
    ],
    [
      classes.select,
      classes.checkbox,
      classes.alarmsSelect,
      alarmsState.filter,
      alarmsState.conditionFilter,
      alarmsState.showSystemAlarms,
      alarmsState.filterOptions.conditions,
      setConditionFilter,
      setAcknowledgeFilter,
      setShowSystemAlarmsFilter,
    ]
  );

  const siteSelectorFilterComponent = useMemo(
    () => (
      <SitesSelector
        userSites={userSites}
        selectedSites={sharedState.sites ?? []}
        setSitesFilter={setSitesFilter}
        showSiteInformationFilters={showSiteInformationFilters}
      />
    ),
    [userSites, sharedState.sites, showSiteInformationFilters, setSitesFilter]
  );

  const filterComponents = useMemo(() => {
    const components = [
      ...(showPeriodFilter ? periodFilterComponent : []),
      ...(showGraphFilters ? graphFilterComponents : []),
      ...(showHistoryFilters ? historyFilterComponents : []),
      ...(showAlarmsFilters ? alarmsFilterComponents : []),
      ...(showAlarmsFilters || showHistoryFilters || showSiteInformationFilters ? [siteSelectorFilterComponent] : []),
    ];

    return (
      <>
        {components.map((component, i) => (
          <div
            key={i}
            className={`flex flex-col self-center ${
              (showAlarmsFilters && alarmFiltersScreenSize) || (showGraphFilters && graphFiltersScreenSize)
                ? 'mb-4'
                : 'mb-4 md:mb-0 mr-2'
            }`}
          >
            {component}
          </div>
        ))}
      </>
    );
  }, [
    showPeriodFilter,
    showGraphFilters,
    showAlarmsFilters,
    showHistoryFilters,
    graphFilterComponents,
    periodFilterComponent,
    graphFiltersScreenSize,
    alarmFiltersScreenSize,
    alarmsFilterComponents,
    historyFilterComponents,
    showSiteInformationFilters,
    siteSelectorFilterComponent,
  ]);

  return (
    <div className="flex flex-row items-center justify-start self-center" data-testid="main-filters-wrapper">
      {smallScreen || showGraphFiltersInMenu || showAlarmsFiltersInMenu ? (
        <>
          <Button
            startIcon={<FilterList />}
            style={{ height: 32 }}
            size="small"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setAnchorEl(event.currentTarget);
            }}
            dataTestId="main-filters-button"
          >
            <Trans>Filters</Trans>
          </Button>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            getContentAnchorEl={null}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            classes={{ paper: 'px-2 pb-1 pt-0' }}
            BackdropProps={{ style: { backgroundColor: 'black', opacity: 0.4 } }}
          >
            <div
              className={`flex flex-col`}
              style={{ width: smallScreen ? 300 : 340, maxWidth: smallScreen ? 300 : 340 }}
            >
              <Typography
                className="hover:text-primaryColor cursor-pointer"
                onClick={() => setAnchorEl(null)}
                style={{ marginLeft: 'auto', marginBottom: 4 }}
              >
                <Trans>OK</Trans>
              </Typography>
              {filterComponents}
            </div>
          </Menu>
        </>
      ) : (
        <div className={`flex flex-row`}>{filterComponents}</div>
      )}
      <Collapse in={(showGraphFilters || showReportingFilters) && sharedState.refreshButton.show} {...{ timeout: 600 }}>
        <div className="flex items-center">
          <Button
            style={{
              height: 32,
              width: smallScreen ? 36 : 92,
              minWidth: 24,
              marginLeft: 4,
            }}
            onClick={sharedState.refreshButton.onClick}
            disabled={sharedState.refreshButton.disabled || !sharedState.refreshButton.show}
            dataTestId="main-filters-refresh-button"
          >
            <Refresh />
            {smallScreen ? '' : <Trans>Refresh</Trans>}
          </Button>
          {showGraphFilters && (
            <CustomTooltip
              tooltipText={
                <div className="my-0.5">
                  <Typography variant="body2">
                    <Trans>Data will appear automatically when selecting the first site.</Trans>
                  </Typography>
                  <br />
                  <Typography variant="body2">
                    <Trans>
                      After making changes to filters or sites selection, please click Refresh button to update the
                      graph.
                    </Trans>
                  </Typography>
                </div>
              }
            />
          )}
        </div>
      </Collapse>
    </div>
  );
};
